/**
 * Validation for the divisions grid's inline cells. Each returns `null` for
 * "do not send" — AG Grid commits on blur, so tabbing through a cell must not
 * fire a PUT.
 */

/**
 * A division must keep a name; clearing the cell is a slip, not an edit.
 * Returns the trimmed name, or null when unchanged/empty.
 */
export const parseDivisionName = (
  value: unknown,
  current: string
): string | null => {
  const next = String(value ?? '').trim()
  if (next === '' || next === current.trim()) {
    return null
  }
  return next
}

/**
 * Standings visibility. The editor yields the strings 'Visible'/'Hidden' (or a
 * real boolean, depending on the editor used), so both shapes are accepted.
 * Returns null when the value did not actually change.
 */
export const parseStandingsVisible = (
  value: unknown,
  current: boolean | null
): boolean | null => {
  const next =
    typeof value === 'boolean'
      ? value
      : String(value ?? '')
          .trim()
          .toLowerCase() === 'visible'

  return next === Boolean(current) ? null : next
}

/**
 * Roster lock date.
 *
 * Distinguishes three outcomes, which a bare `string | null` could not:
 * - `{ value: 'YYYY-MM-DD' }` — set or changed
 * - `{ value: null }`         — deliberately CLEARED, and worth sending
 * - `null`                    — unchanged or unparseable, send nothing
 *
 * The API stores a date-only string; anything with a time component is
 * truncated so a round-trip cannot drift by a timezone.
 */
export const parseLockRosterDate = (
  value: unknown,
  current: string | null
): { value: string | null } | null => {
  const raw = String(value ?? '').trim()
  const currentDay = (current ?? '').split(/[T ]/)[0]

  if (raw === '') {
    // Clearing an already-empty cell is a no-op, not an edit.
    return currentDay === '' ? null : { value: null }
  }

  const day = raw.split(/[T ]/)[0]
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return null
  }
  if (day === currentDay) {
    return null
  }
  return { value: day }
}
